import React from 'react';
import { graphql, useFragment } from 'react-relay';
import type { FragmentRefs } from 'relay-runtime';
import { OriginalEstimateInlineEditViewIsEditable } from '@atlassian/jira-issue-field-original-estimate-inline-edit-full/src/ui/original-estimate/index.tsx';
import type { OriginalEstimateCell_OriginalEstimateTimeTrackingFieldCell$key } from '@atlassian/jira-relay/src/__generated__/OriginalEstimateCell_OriginalEstimateTimeTrackingFieldCell.graphql';
import type { OriginalEstimateCell$key } from '@atlassian/jira-relay/src/__generated__/OriginalEstimateCell.graphql';
import type { OriginalEstimateCell_OriginalEstimateCellNew$key } from '@atlassian/jira-relay/src/__generated__/OriginalEstimateCell_OriginalEstimateCellNew.graphql';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import {
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';

type OriginalEstimateFieldRef = {
	readonly ' $fragmentSpreads': FragmentRefs<
		'OriginalEstimateCell' | 'OriginalEstimateCell_OriginalEstimateCellNew'
	>;
};

export type Props = {
	fieldRef: OriginalEstimateFieldRef;
	isReadOnlyWithoutHoverPopover?: boolean;
};

const OriginalEstimateCellOld = ({ fieldRef, isReadOnlyWithoutHoverPopover }: Props) => {
	// NOTE: original estimate type is used to get timeTrackingField data, which is used in underlying original estimate decomposed field.
	const data = useFragment<OriginalEstimateCell$key>(
		graphql`
			fragment OriginalEstimateCell on JiraOriginalTimeEstimateField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				# eslint-disable-next-line @atlassian/relay/unused-fields
				id
				issue {
					timeTrackingField {
						...OriginalEstimateCell_OriginalEstimateTimeTrackingFieldCell
							@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
					}
				}
			}
		`,
		fieldRef,
	);

	return (
		<OriginalEstimateTimeTrackingFieldCell
			fieldRef={data.issue?.timeTrackingField ?? null}
			isReadOnlyWithoutHoverPopover={isReadOnlyWithoutHoverPopover}
			issueFieldId={data.id}
		/>
	);
};

const OriginalEstimateCellNew = ({ fieldRef, isReadOnlyWithoutHoverPopover }: Props) => {
	// NOTE: original estimate type is used to get timeTrackingField data, which is used in underlying original estimate decomposed field.
	const data = useFragment<OriginalEstimateCell_OriginalEstimateCellNew$key>(
		graphql`
			fragment OriginalEstimateCell_OriginalEstimateCellNew on JiraOriginalTimeEstimateField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				# eslint-disable-next-line @atlassian/relay/unused-fields
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				issue {
					timeTrackingField {
						...originalEstimate_issueFieldOriginalEstimateInlineEditFull_OriginalEstimateInlineEditViewIsEditable_fragmentRef
					}
				}
			}
		`,
		fieldRef,
	);

	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();
	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		data && 'lazyIsEditableInIssueView' in data && isInlineEditingExtendedFieldSupportEnabled
			? data.lazyIsEditableInIssueView
			: false;
	useLazyLoadEditability(data?.id, isEditable);

	const isSingleLineRowHeightEnabled =
		useIsSingleLineRowHeightEnabled() && !isReadOnlyWithoutHoverPopover;

	return (
		<OriginalEstimateInlineEditViewIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data?.issue?.timeTrackingField ?? null}
			isEditable={(isEditable && !isReadOnlyWithoutHoverPopover) ?? false}
			readViewFitContainerHeight
			shouldRenderHoverPopover={isSingleLineRowHeightEnabled}
			issueFieldId={data.id}
		/>
	);
};

export const OriginalEstimateCell = componentWithCondition(
	() => fg('jira-original-estimate-editable-fix'),
	OriginalEstimateCellNew,
	OriginalEstimateCellOld,
);

const OriginalEstimateTimeTrackingFieldCell = ({
	fieldRef,
	isReadOnlyWithoutHoverPopover,
	issueFieldId,
}: {
	fieldRef: OriginalEstimateCell_OriginalEstimateTimeTrackingFieldCell$key | null;
	isReadOnlyWithoutHoverPopover?: boolean;
	issueFieldId?: string;
}) => {
	const data = useFragment<OriginalEstimateCell_OriginalEstimateTimeTrackingFieldCell$key>(
		graphql`
			fragment OriginalEstimateCell_OriginalEstimateTimeTrackingFieldCell on JiraTimeTrackingField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...originalEstimate_issueFieldOriginalEstimateInlineEditFull_OriginalEstimateInlineEditViewIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();
	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		data && 'lazyIsEditableInIssueView' in data && isInlineEditingExtendedFieldSupportEnabled
			? data.lazyIsEditableInIssueView
			: false;
	useLazyLoadEditability(data?.id, isEditable);

	const isSingleLineRowHeightEnabled =
		useIsSingleLineRowHeightEnabled() && !isReadOnlyWithoutHoverPopover;

	return (
		<OriginalEstimateInlineEditViewIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data ?? null}
			isEditable={(isEditable && !isReadOnlyWithoutHoverPopover) ?? false}
			readViewFitContainerHeight
			shouldRenderHoverPopover={isSingleLineRowHeightEnabled}
			issueFieldId={issueFieldId}
		/>
	);
};
